import * as React from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import ReactYoutubePlayer from "react-player/youtube";
import ReactSoundcloudPlayer from "react-player/soundcloud";
import ReactMixcloudPlayer from "react-player/mixcloud";
import { Box, Grid } from "@mui/material";
import { InfoOutlined, PlayCircle, EmojiEvents } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useIsMobile from "hooks/custom/useIsMobile";

import LocalStorage from "utils/localStorage";
import {
  AdsComponent,
  SuggestedVideos,
  Tooltip,
  TwitchPlayer,
} from "components";
import Logo from "assets/logos/IsoFroinBlanco.png";
import {
  formatTokenVideo,
  returnApiError,
  returnNumFormated,
  unformatTokenVideo,
} from "utils/validations";
import { databaseService, firestoreService } from "utils/firebase";
import pageStyles from "./DetailVideoStyles";
import SadImage from "assets/icons/sad.svg";
import { Store } from "hooks/main_store";
import { VideoInfoProps } from "models";
import Model from "hooks/Model";
import Api from "utils/api";
import { useRanking } from "services";
import { detectIncognito } from "utils/detectIncognito";
import * as moment from "moment";

const PROGRESS_INTERVAL = 1000;

interface OwnerInfoProps {
  image?: string;
  name?: string;
}

function DetailVideoScreen() {
  const { state } = useContext(Store);
  const styles = pageStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { settings } = useRanking({});

  const [rewards, setRewards] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [loading, setLoading] = useState(true);
  const [played, setPlayed] = useState(false);
  const [continueAutoplayed, setContinueAutoplayed] = useState(false);
  const [firstPlay, setFirstPlay] = useState(false);
  const [videoInfo, setVideoInfo] = useState<VideoInfoProps>({});
  const [ownerInfo, setOwnerInfo] = useState<OwnerInfoProps>({});
  const [recommendedVideos, setRecommendedVideos] = useState([]);
  const [videoLoadStatus, setVideoLoadStatus] = useState("");
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const idContent = React.useMemo(
    () => searchParams.get("id") || recommendedVideos?.[0]?.id || "",
    [recommendedVideos, searchParams],
  );
  const isAutoplayed = React.useMemo(
    () => searchParams.get("autoplay") === "true",
    [searchParams],
  );

  useIsMobile(setIsMobile);

  const updateRewards = () => {
    if (played) {
      setRewards((oldValue) => {
        const newValue = oldValue + 1;

        if (videoInfo?.limit > 0 && videoInfo?.limit < newValue) {
          setPlayed(false);
          // if (isAutoplayed) {
          setContinueAutoplayed(true);
          // } else {
          //   setShowLimitAlert(true);
          // }
          return videoInfo.limit;
        }

        return newValue;
      });
    }
  };

  const setLocalInfo = useCallback(async () => {
    const result = await detectIncognito();
    LocalStorage(result.isPrivate).setItem("currentPlayed", idContent);
    LocalStorage(result.isPrivate).setItem(
      "localSecurityToken",
      formatTokenVideo(String(rewards)),
    );
  }, [rewards, idContent]);

  const resetLocalInfo = async () => {
    const result = await detectIncognito();
    LocalStorage(result.isPrivate).removeItem("currentPlayed");
    LocalStorage(result.isPrivate).removeItem("localSecurityToken");
  };

  const getLastRewards = async () => {
    try {
      const result = await detectIncognito();
      const localCurrentPlayed = LocalStorage(result.isPrivate).getItem(
        "currentPlayed",
      );
      const localSecurityToken = LocalStorage(result.isPrivate).getItem(
        "localSecurityToken",
      );

      if (
        localSecurityToken &&
        localCurrentPlayed === idContent &&
        rewards === 0
      ) {
        const lastRewards = unformatTokenVideo(localSecurityToken);
        setRewards(parseInt(lastRewards));
      }
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  const onReplace = async () => {
    try {
      Model.setData("loading", true);
      Model.setData("dialog", {
        open: false,
      });

      const response = await Api.post("/ranking/vwin/replaceCurrentVideo", {
        id: idContent,
      });

      if (response.ok) {
        if (response.data === "available") {
          setPlayed(true);

          if (!firstPlay && rewards === 0) {
            setRewards(1);
          }

          setFirstPlay(true);
        } else if (response.data === "cant-play") {
          setVideoLoadStatus("cant-play");
          setPlayed(false);
        } else {
          throw new Error("Error desconocido");
        }
      } else {
        throw new Error(returnApiError(response));
      }
    } catch (error) {
      setPlayed(false);
      setVideoLoadStatus("error");
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  const confirmReplace = (currentPlayed: string) => {
    Model.setData("dialog", {
      open: true,
      title: "Ups ... no puedes continuar",
      text: "Actualmente estás visualizando otro contenido en este momento. Por seguridad solo puedes ver un contenido a la vez para generar recompensas. ¿Quieres ver este nuevo contenido e inhabilitar el anterior por 24 horas?",
      txtLeft: t("commons.cancel"),
      fnLeft: () => {
        Model.setData("dialog", { open: false });
        setRewards(0);
        setLoading(true);
        setTimeout(() => {
          setLoading(false);
        }, 500);
      },
      txtRight: t("commons.confirm"),
      fnRight: onReplace,
      txtAux: "Ir al contenido",
      fnAux: () => {
        Model.setData("dialog", { open: false });
        navigate(`/detail-video?id=${currentPlayed}`);
      },
    });
  };

  const onPlay = useCallback(async () => {
    try {
      Model.setData("loading", true);
      Model.logMetaEvent({
        data: [
          {
            event_name: "Play",
            event_time: parseInt(moment().format("X")),
            action_source: "website",
            original_event_data: {
              event_name: "Play",
              event_time: parseInt(moment().format("X")),
            },
            user_data: {
              client_user_agent: navigator.userAgent,
              em: [null],
              ph: [null],
            },
          },
        ],
      });

      if (state.infoUser?.email) {
        const response = await Api.post("/ranking/vwin/verifyBeforeWatch", {
          id: idContent,
        });

        if (response.ok) {
          if (response.data === "available") {
            setPlayed(true);

            if (!firstPlay && rewards === 0) {
              setRewards(1);
            }

            setFirstPlay(true);
          } else if (response.data === "has-other") {
            const currentPlayed = (
              await databaseService.getDatabase(
                `history/${state.infoUser.uid}/currentPlayed`,
              )
            ).val();
            confirmReplace(currentPlayed);
          } else if (response.data === "cant-play") {
            setVideoLoadStatus("cant-play");
            setPlayed(false);
          } else {
            throw new Error("Error desconocido");
          }
        } else {
          throw new Error(returnApiError(response));
        }
      } else {
        throw new Error("Debes iniciar sesión para participar");
      }
    } catch (error) {
      setPlayed(false);
      setVideoLoadStatus(
        error?.message === "Debes iniciar sesión para participar"
          ? "need-auth"
          : "error",
      );
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
    // eslint-disable-next-line
  }, [state.infoUser, firstPlay, videoInfo]);

  const getRecommendedVideos = async (owner, onlyReturn) => {
    try {
      if (owner) {
        const ownerInfoResponse = (
          await firestoreService.getDoc("channels", owner)
        ).data();
        const recommendedVideosResponse = [];
        const getVideosResponse = await firestoreService.getQuery("videos", [
          { label: "owner", condition: "==", value: owner },
        ]);

        getVideosResponse.forEach((video) => {
          if (idContent !== video.id && video.data()?.hide !== true) {
            recommendedVideosResponse.push({
              ...(video.data() || {}),
              id: video.id,
            });
          }
        });

        setOwnerInfo(ownerInfoResponse);

        if (onlyReturn) {
          return recommendedVideosResponse
            .map((value) => ({ ...value, sort: Math.random() }))
            .sort((a, b) => a.sort - b.sort);
        }
        setRecommendedVideos(
          recommendedVideosResponse
            .map((value) => ({ ...value, sort: Math.random() }))
            .sort((a, b) => a.sort - b.sort),
        );
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    }
  };

  const saveRewards = async () => {
    try {
      Model.setData("dialog", { open: false });
      Model.setData("loading", true);
      const response = await Api.post("/ranking/vwin/saveScore", {
        foints: rewards,
        id: idContent,
      });

      if (response.ok && response.data === "saved") {
        setPlayed(false);
        setRewards(0);
        setLoading(true);
        await resetLocalInfo();

        Model.updateAlerts({
          message: "Puntos acumulados éxistosamente",
          variant: "success",
        });

        const recommendedVideos = await getRecommendedVideos("froin", true);
        let filteredRecommendedVideos = recommendedVideos?.filter(
          (item) => !showWaiting(item.id),
        );
        if ((filteredRecommendedVideos?.length || 0) < 1) {
          filteredRecommendedVideos = recommendedVideos;
        }

        navigate(
          `/detail-video?id=${filteredRecommendedVideos?.[0]?.id}&autoplay=true`,
        );
      } else if (response.ok && response.data === "has-other") {
        Model.updateAlerts({
          message:
            "Actualmente estas viendo otro contenido y no será posible guardar estos puntos",
          variant: "error",
        });
      } else if (response.ok && response.data === "ranking-finished") {
        Model.updateAlerts({
          message:
            "Aún no puedes acumular puntos para el ranking porque ya ha finalizado",
          variant: "error",
        });
      } else {
        throw new Error(returnApiError(response));
      }

      const rankingHistory =
        (
          await databaseService.getDatabase(`history/${state.infoUser.uid}`)
        ).val() || {};

      Model.setData("rankingHistory", rankingHistory);
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      setTimeout(() => {
        Model.setData("loading", false);
        setLoading(false);
      }, 1000);
    }
  };

  const onRequestRewards = () => {
    if (rewards > 0) {
      if (settings.finished) {
        Model.setData("dialog", {
          open: true,
          title: "Ranking finalizado",
          text: "Aún no puedes acumular puntos para el ranking porque ya ha finalizado, pronto enviaremos la notificación para la próxima fecha de inicio y la descripción del premio.",
          txtRight: "Ok",
          fnRight: () => Model.setData("dialog", { open: false }),
        });
      } else {
        Model.setData("dialog", {
          open: true,
          title: t("components.header.beforeContinue"),
          text: `¿Estas de acuerdo en reclamar estas recompensas por hoy? ${
            videoInfo.provider === "twitch"
              ? ""
              : "Recuerda que solo puedes reproducir cada contenido una vez al día."
          }`,
          txtLeft: t("commons.cancel"),
          fnLeft: () => Model.setData("dialog", { open: false }),
          txtRight: t("commons.confirm"),
          fnRight: saveRewards,
        });
      }
    } else {
      Model.updateAlerts({
        message:
          "Debes generar más puntos, solo reproduce el contenido del artista",
        variant: "error",
      });
    }
  };

  const renderPreviewInfo = () => (
    <div className={styles.containerInfoVideo} id="detail-video-info">
      <Grid
        container
        wrap="nowrap"
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item>
          <img
            alt="author"
            src={videoInfo?.artistImage || ownerInfo?.image || Logo}
            className={styles.imageAuthor}
          />
          <PlayCircle className={styles.iconPlayer} />
        </Grid>
        <Grid
          item
          flex={1}
          display={"flex"}
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <p className={styles.nameAuthor}>{videoInfo?.name}</p>
          <button
            style={{ display: "none" }}
            onClick={beforeToExit(() =>
              navigate(`/profile-videos?channel=${videoInfo?.owner}`),
            )}
          >
            <p className={styles.labelHost}>Hosted by:</p>
            <p>{ownerInfo?.name || "No Name"}</p>
          </button>
          <p className={styles.description}>{videoInfo?.description}</p>
        </Grid>
      </Grid>
    </div>
  );

  const renderContentVideo = () => {
    switch (videoLoadStatus || videoInfo?.provider) {
      case "mixcloud":
        return (
          <ReactMixcloudPlayer
            url={videoInfo?.url}
            light
            width="100%"
            height="100%"
            progressInterval={PROGRESS_INTERVAL}
            onProgress={updateRewards}
            onPause={() => setPlayed(false)}
            onEnded={() => setPlayed(false)}
            onError={() => setPlayed(false)}
            onPlay={onPlay}
            playIcon={renderPreviewInfo()}
            config={{
              options: {
                allow: "autoplay",
                autoplay: 1,
              },
            }}
          />
        );
      case "soundcloud":
        return (
          <ReactSoundcloudPlayer
            url={videoInfo?.url}
            light
            width="100%"
            height="100%"
            progressInterval={PROGRESS_INTERVAL}
            onProgress={updateRewards}
            onPause={() => setPlayed(false)}
            onEnded={() => setPlayed(false)}
            onError={() => setPlayed(false)}
            onPlay={onPlay}
            playIcon={renderPreviewInfo()}
            config={{
              options: {
                autoplay: 1,
              },
            }}
          />
        );
      case "youtube":
        return (
          <ReactYoutubePlayer
            url={videoInfo?.url}
            config={{
              playerVars: { showinfo: 0, controls: 1, autoplay: 1 },
            }}
            light
            width="100%"
            height="100%"
            progressInterval={PROGRESS_INTERVAL}
            onProgress={updateRewards}
            onPause={() => setPlayed(false)}
            onEnded={() => {
              setPlayed(false);
              setContinueAutoplayed(true);
            }}
            onError={() => setPlayed(false)}
            onPlay={onPlay}
            playIcon={renderPreviewInfo()}
          />
        );
      case "twitch":
        return (
          <TwitchPlayer
            url={videoInfo?.url}
            width="100%"
            height="100%"
            progressInterval={PROGRESS_INTERVAL}
            onProgress={updateRewards}
            onPause={() => setPlayed(false)}
            onEnded={() => setPlayed(false)}
            onError={() => setPlayed(false)}
            onPlay={onPlay}
          />
        );
      case "need-auth":
        return (
          <div className={styles.contentErrorVideo}>
            <img
              alt="Error load video"
              className={styles.errorVideoIcon}
              src={SadImage}
            />
            <p className={styles.errorVideoText}>
              Antes ver este contenido, y acumular puntos para el ranking de
              premios, debes haber iniciado sesión en tu cuenta.{" "}
              <span onClick={() => Model.setData("showAuth", true)}>
                Hazlo aquí.
              </span>
            </p>
          </div>
        );
      case "cant-play":
        return (
          <div className={styles.contentErrorVideo}>
            <img
              alt="Error load video"
              className={styles.errorVideoIcon}
              src={SadImage}
            />
            <p className={styles.errorVideoText}>
              Ya has visualizado este contenido en las útlimas 24 horas, vuelve
              luego para reproducirlo de nuevo.
            </p>
          </div>
        );
      default:
        return (
          <div className={styles.contentErrorVideo}>
            <img
              alt="Error load video"
              className={styles.errorVideoIcon}
              src={SadImage}
            />
            <p className={styles.errorVideoText}>
              Ups ... Hubo un problema y no podemos cargar el video, por favor
              revisa que no tienes otra sesión abierta. Si el problema persiste
              y eres el dueño de este video por favor contáctanos.
            </p>
          </div>
        );
    }
  };

  const showWaiting = useCallback(
    (id: string) => {
      const lastDatePlayed = state.rankingHistory?.[id];
      const countSeconds = moment().diff(
        moment(lastDatePlayed, "x"),
        "seconds",
      );

      return lastDatePlayed && countSeconds < 86400;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [state.rankingHistory],
  );

  const firstLoad = async (videoId: string) => {
    try {
      setLoading(true);
      Model.setData("loading", true);
      if (videoId) {
        const videoInfoResponse = (
          await firestoreService.getDoc("videos", videoId)
        ).data();

        if (videoInfoResponse) {
          setVideoInfo(videoInfoResponse);
          await getRecommendedVideos(videoInfoResponse?.owner, false);
        } else {
          navigate("/");
        }

        setLoading(false);
        Model.setData("loading", false);
      } else {
        const recommendedVideos = await getRecommendedVideos("froin", true);
        let filteredRecommendedVideos = recommendedVideos?.filter(
          (item) => !showWaiting(item.id),
        );
        if ((filteredRecommendedVideos?.length || 0) < 1) {
          filteredRecommendedVideos = recommendedVideos;
        }

        window.location.search = `id=${filteredRecommendedVideos?.[0]?.id}${searchParams.get("origin") ? `&origin=${searchParams.get("origin")}` : ""}`;
      }
    } catch (error) {
      setLoading(false);
      Model.setData("loading", false);
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    }
  };

  const beforeToExit = (callback: () => void) => () => {
    if (rewards > 0) {
      Model.setData("dialog", {
        open: true,
        title: "No olvides reclamar tu puntaje",
        text: "Aún tienes recompensas por reclamar, ¿estas de acuerdo en salir? Tus puntos no se recuperarán y tendrás que ver de nuevo el contenido.",
        txtLeft: t("commons.cancel"),
        fnLeft: () => Model.setData("dialog", { open: false }),
        txtRight: t("commons.confirm"),
        fnRight: () => {
          Model.setData("dialog", {
            open: false,
          });
          Model.setData("loading", true);
          callback();
        },
      });
    } else {
      callback?.();
    }
  };

  const returnAfterDate = useCallback(() => {
    const lastDatePlayed = state.rankingHistory?.[idContent];
    const countSeconds = moment().diff(moment(lastDatePlayed, "x"), "seconds");

    if (lastDatePlayed && countSeconds < 86400) {
      return moment(lastDatePlayed, "x")
        .add(24, "hours")
        .format("DD/MM/YYYY, hh:mma");
    }
    return "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.rankingHistory]);

  const goToRewards = () => {
    if (isMobile) {
      const targetDiv = document.getElementById("btn-contact-artist");
      targetDiv.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (showLimitAlert) {
      setPlayed(false);
      setLoading(true);

      Model.setData("dialog", {
        open: true,
        title: "Límite alcanzado",
        text: "Ya has acumulado la máxima cantidad de puntos para este contenido por hoy, te invitamos a que sigas visualizando más artistas.\n\nRECUERDA: Reclama tus puntos antes de salir de esta vista.",
        txtRight: t("commons.confirm"),
        onClose: () => {
          setShowLimitAlert(false);
          setLoading(false);
        },
        fnRight: () => {
          setShowLimitAlert(false);
          Model.setData("dialog", {
            open: false,
          });
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLimitAlert]);

  useEffect(() => {
    const unloadCallback = (event) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    if (rewards > 0) {
      if (rewards % 50 === 0) {
        setLocalInfo();
      }
      window.addEventListener("beforeunload", unloadCallback);
    } else {
      window.removeEventListener("beforeunload", unloadCallback);
    }

    return () => window.removeEventListener("beforeunload", unloadCallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewards]);

  useEffect(() => {
    if (state.infoUser?.uid && rewards === 0) {
      getLastRewards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.infoUser]);

  useEffect(() => {
    Model.setData("layoutConfig", { withBg: true, beforeToExit: beforeToExit });

    firstLoad(idContent);
    setPlayed(false);
    setRewards(0);
    setFirstPlay(false);
    setVideoLoadStatus("");
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idContent, state.rankingHistory]);

  useEffect(() => {
    if (!loading && !state.loading) {
      Model.setData("stepTutorial", "detail-video");
    }
  }, [loading, state.loading]);

  useEffect(() => {
    if (played) {
      goToRewards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [played]);

  useEffect(() => {
    if (isAutoplayed && state.infoUser?.uid) {
      setTimeout(() => {
        document.getElementById("detail-video-info")?.click?.();
        onPlay();
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoplayed, state.infoUser]);

  useEffect(() => {
    if (continueAutoplayed && !settings.finished) {
      saveRewards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continueAutoplayed, settings]);

  return (
    <>
      <Grid
        container
        gap={6}
        className={styles.gridContainerInfo}
        flexWrap="nowrap"
      >
        <Grid item flex={1}>
          <p className={styles.nameVideo}>Reproduce y Gana</p>

          <Grid
            container
            wrap="wrap"
            justifyContent={{ xs: "center", md: "start" }}
            alignItems="center"
            position="relative"
            className={[
              styles.containerRewards,
              played ? styles.containerRewardsActive : "",
            ].join(" ")}
            gap={1}
            id="detail-video-rewards"
          >
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-around"}
              position={"absolute"}
              bottom={"6px"}
              left={"30px"}
              width={"calc(100% - 60px)"}
              gap={3}
            >
              <p
                className={styles.labelInSection}
                onClick={() => setShowTooltip((prev) => !prev)}
              >
                Recompensas{" "}
                <Tooltip
                  title={"Así de fácil ganas"}
                  text={
                    "Mientras ves contenido irás generando puntos, los cuales funcionan para subir tu posición en el Ranking y alcanzar a obtener tu parte de la bolsa de premios. Recuerda que cada contenido tiene un límite máximo para generar puntos."
                  }
                  open={showTooltip}
                  isIcon
                >
                  <InfoOutlined style={{ fontSize: 14 }} />
                </Tooltip>
              </p>

              <button
                className={[
                  styles.requestRewards,
                  rewards > 0 ? styles.availableReward : "",
                ].join(" ")}
                onClick={onRequestRewards}
              >
                <EmojiEvents /> <span>Reclamar</span>
              </button>
            </Box>
            <Grid
              item
              display="flex"
              flexDirection="row"
              alignItems="center"
              flex={1}
            >
              <p className={styles.totalRewards}>
                {returnNumFormated(rewards)}
              </p>
            </Grid>
            <Grid item>
              <img
                alt="FROIN"
                src={Logo}
                className={[
                  styles.iconTotalRewards,
                  played ? styles.loadPoint : "",
                ].join(" ")}
              />
            </Grid>
            <Grid item flex={1} alignItems="center">
              <p className={styles.labelTotalRewards}>Puntos</p>
              {videoInfo?.limit ? (
                <p className={styles.labelLimit}>
                  Máx. {returnNumFormated(videoInfo?.limit)} puntos
                </p>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>

          <div className={styles.containerSuggested}>
            <SuggestedVideos
              title={"Puedes continuar viendo"}
              data={recommendedVideos}
              beforeToExit={beforeToExit}
            />
          </div>
        </Grid>
        <Grid
          item
          flex={1}
          marginTop={{ xs: 2, md: 0 }}
          className={styles.cardRewards}
        >
          {videoInfo.provider === "twitch" ? (
            <Box className={styles.contInfoMessage}>
              <InfoOutlined />{" "}
              <p className={styles.warningMessage}>
                <b>Versión Beta:</b> Este contenido lo puedes visualizar sin
                limite
              </p>
            </Box>
          ) : returnAfterDate() ? (
            <Box className={styles.contInfoMessage}>
              <InfoOutlined />{" "}
              <p className={styles.warningMessage}>
                <b>Aún no puedes ver este contenido:</b> Podrás reproducirlo
                después del <b>{returnAfterDate()}</b>
              </p>
            </Box>
          ) : (
            <Box className={styles.contWarningMessage}>
              <InfoOutlined />{" "}
              <p className={styles.warningMessage}>
                <b>RECUERDA:</b> No te salgas de esta página, si reproduces el
                contenido fuera no te contará los puntos para tu puntaje en el
                Ranking de premios
              </p>
            </Box>
          )}

          <div
            className={styles.containerVideo}
            id="detail-video-container-video"
            style={
              videoInfo?.imageUrl
                ? { backgroundImage: `url(${videoInfo?.imageUrl})` }
                : {}
            }
          >
            {loading ? <></> : renderContentVideo()}
          </div>

          {videoInfo?.urlSocial ? (
            <a
              className={styles.linkArtist}
              href={videoInfo?.urlSocial}
              target="_blank"
              rel="noreferrer"
              id="btn-contact-artist"
            >
              {"⭐"} Contactar artista {"⭐"}
            </a>
          ) : (
            <></>
          )}

          {searchParams.get("origin") === "riffwin" ? (
            <a
              className={styles.linkArtist}
              href="https://riff.win"
              rel="noreferrer"
            >
              Volver a RiffWin
            </a>
          ) : (
            <></>
          )}

          {videoInfo.provider === "twitch" ? (
            <iframe
              src={`https://www.twitch.tv/embed/${videoInfo?.url}/chat?parent=localhost&parent=froin.club&parent=www.foin.club&parent=test.froin.club`}
              title="chat"
              className={styles.containerChat}
            />
          ) : (
            <></>
          )}
        </Grid>
      </Grid>

      <Box sx={{ width: "100%" }}>
        <AdsComponent />
      </Box>
    </>
  );
}

export default DetailVideoScreen;
